import { useState, type ReactElement } from 'react';

import { stageKindLabel } from '@/lib/stage-kind';
import { CardFrame, QueryBody } from '@/components/ui/card-frame';

import { formatHours } from '../presentation';
import { FLOW_KIND_LABELS, standardVerb, workLabel } from '../standards-form';
import { STANDARD_STAGES, type StandardsRow } from '../types';
import { useStandardsMatrix } from '../use-time-study';
import { AdjustStandardModal } from './AdjustStandardModal';
import { StandardHistoryModal } from './StandardHistoryModal';

// Pestaña Estándares (so=420, spec §4.2 y D5; so=340): matriz vigente por tipo de
// documento y tipo de trabajo, en horas y con su total. Con permiso de gestión, "Ajustar"
// abre el diálogo; "Historial" muestra las versiones de ese tipo de trabajo.

const hours = (minutes: number | null | undefined): string =>
  minutes === null || minutes === undefined ? 'Sin estándar' : formatHours(minutes * 60);

const capitalized = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export function StandardsTab({
  companyId,
  canManage,
}: {
  companyId: string;
  canManage: boolean;
}): ReactElement {
  const query = useStandardsMatrix({ companyId, enabled: true });
  const [adjusting, setAdjusting] = useState<StandardsRow | null>(null);
  const [history, setHistory] = useState<StandardsRow | null>(null);

  return (
    <CardFrame title="Estándares de producción vigentes" count="horas por etapa">
      <QueryBody
        query={query}
        rows={4}
        isEmpty={(d) => d.length === 0}
        emptyText="No hay tipos de documento activos. Créalos en los catálogos de calidad."
      >
        {(rows) => (
          <div className="t-scroll">
            <table className="t">
              <caption className="sr-only">Estándares vigentes por tipo de trabajo</caption>
              <thead>
                <tr>
                  <th scope="col">Tipo de trabajo</th>
                  {STANDARD_STAGES.map((s) => (
                    <th key={s} scope="col">
                      {stageKindLabel(s)}
                    </th>
                  ))}
                  <th scope="col">Total</th>
                  <th scope="col">
                    <span className="sr-only">Acciones</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const label = workLabel(r.documentTypeName, r.flowKind);
                  return (
                    <tr key={`${r.documentTypeId}:${r.flowKind}`}>
                      <th scope="row">
                        {r.documentTypeName} · {capitalized(FLOW_KIND_LABELS[r.flowKind])}
                      </th>
                      {STANDARD_STAGES.map((s) => (
                        <td key={s} className="dim-mono">
                          {hours(r.stages[s]?.standardMinutes)}
                        </td>
                      ))}
                      <td className="dim-mono">
                        {r.totalMinutes === null ? '—' : formatHours(r.totalMinutes * 60)}
                      </td>
                      <td>
                        <div className="stdacts">
                          {canManage && (
                            <button
                              type="button"
                              className="btn sm"
                              aria-label={`${standardVerb(r)} ${label}`}
                              onClick={() => setAdjusting(r)}
                            >
                              {standardVerb(r)}
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn mut sm"
                            aria-label={`Historial ${label}`}
                            onClick={() => setHistory(r)}
                          >
                            Historial
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </QueryBody>
      <AdjustStandardModal
        row={adjusting}
        companyId={companyId}
        onClose={() => setAdjusting(null)}
      />
      <StandardHistoryModal row={history} companyId={companyId} onClose={() => setHistory(null)} />
    </CardFrame>
  );
}
